<template>
    <MainHeaderComponent v-if="dataIsLoaded && showHeader" :activePage="currentActivePage"/>
    <router-view v-if="dataIsLoaded" v-slot="{ Component }">
        <div :class="[showSidebar ? 'pt-[3.75rem]' : '', showSidebar && !sidebarCollapsed ? 'pl-52' : '']">
            <keep-alive include="KassaView">
                <component :is="Component"/>
            </keep-alive>
        </div>
    </router-view>
    <LoadingComponent v-else/>
</template>


<script>
    import {mapState} from 'vuex';
    import {ROLES, UPDATE_DATA_INTERVAL} from "@/constants";
    import LoadingComponent from '@/components/LoadingComponent.vue';
    import MainHeaderComponent from '@/components/MainHeaderComponent.vue';
    import i18n from "@/i18n";
    import {Capacitor} from '@capacitor/core';

    export default {
        name: "App",
        components: {LoadingComponent, MainHeaderComponent},
        data() {
            return {
                CONSTANTS: {
                    ROLES: ROLES,
                },
                dataIsLoaded: false,
                sidebarCollapsed: localStorage.getItem("sidebarCollapsed") === "true",
            }
        },
        computed: {
            ...mapState("authentication", ["isMobileVersion", "isPWA", "isAuthorized"]),
            ...mapState("personalData", ["role", "personalData"]),
            ...mapState("company", ["companyData"]),
            ...mapState("cashRegister", ["cashRegisterData"]),
            currentActivePage() {
                const routeToPage = {
                    "dashboard": "cash-register",
                    "cash-register": "cash-register",
                    "sales-search": "sales-search",
                    "appointments": "appointments",
                    "clients": "clients",
                    "catalog": "catalog",
                    "staffs": "staffs",
                    "schedule-and-salary": "staffs",
                    "statistics": "statistics",
                    "pricing": "pricing",
                    "account": "account",
                    "ai-concierge": "ai-concierge",
                };
                return routeToPage[this.$route?.name] || "";
            },
            showHeader() {
                if (!this.isAuthorized) return false;
                const noHeaderRoutes = ["public-booking", "public-booking-cancel", "public-booking-confirm", "sign-in", "sign-up", "account-confirmation", "reset-password", "page-not-found", "role", "choose-company", "create-company", "sign-out"];
                return !noHeaderRoutes.includes(this.$route?.name);
            },
            showSidebar() {
                if (this.isMobileVersion) return false;
                if (!this.isAuthorized) return false;
                const noSidebarRoutes = ["public-booking", "public-booking-cancel", "public-booking-confirm", "sign-in", "sign-up", "account-confirmation", "reset-password", "page-not-found", "role", "choose-company", "create-company", "sign-out"];
                return !noSidebarRoutes.includes(this.$route?.name);
            },
        },
        watch: {
            // Подключить SSE когда кассовый регистр загрузится
            cashRegisterData() {
                this.tryConnectSSE();
            },
            // Также при изменении isAuthorized
            isAuthorized(newValue) {
                if (newValue) {
                    this.tryConnectSSE();
                }
            }
        },
        methods: {
            startDataPolling() {
                if (this._dataPollingIntervalId) return;
                this._dataPollingIntervalId = setInterval(() => {
                    if (!this.isAuthorized) return;
                    if (!this.$store.state.personalData.role) return;
                    const lastFilters = this.$store.state.invoice.lastFilters;
                    if (lastFilters && this.cashRegisterData?.id) {
                        this.$store.dispatch("invoice/getInvoices", {
                            dateFrom: lastFilters.dateFrom,
                            dateTo: lastFilters.dateTo,
                        });
                    }
                }, 5 * 60_000);
            },
            tryConnectSSE() {
                const shopId = this.$store.state.cashRegister.cashRegisterData?.id;
                const isAuthorized = this.$store.state.authentication.isAuthorized;
                const isConnected = this.$store.state.salesSSE.connected;

                if (isAuthorized && shopId && !isConnected) {
                    this.$store.dispatch('salesSSE/connect');
                }
            },
            async initNativePlugins() {
                if (!Capacitor.isNativePlatform()) return;
                const {StatusBar, Style} = await import('@capacitor/status-bar');
                const {Keyboard, KeyboardResize} = await import('@capacitor/keyboard');
                await StatusBar.setStyle({style: Style.Dark});
                Keyboard.setAccessoryBarVisible({isVisible: true});
                Keyboard.setResizeMode({mode: KeyboardResize.None});
            },
            async hideNativeSplashScreen() {
                if (!Capacitor.isNativePlatform()) return;
                const {SplashScreen} = await import('@capacitor/splash-screen');
                await SplashScreen.hide();
            },
            autoDetectUserLanguage() {
                // Intilly is English-first. Honor an explicit user choice via the
                // ui-language cookie if set, otherwise default to en regardless of
                // navigator.language.
                const savedLanguage = this.$globalMethods.getCookie("ui-language");
                i18n.global.locale.value = savedLanguage === "ru" ? "ru" : "en";
            },
            async loadData() {
                const firstDayOfCurrentMonth = this.$globalMethods.getFirstDayOfCurrentMonth();
                const routingName = this.$route.name;
                const unauthorizedAccessUrls = ["sign-up", "sign-in", "account-confirmation", "reset-password", "reset-phone", "reset-email-continue", "reset-email-final", "public-booking", "public-booking-cancel", "public-booking-confirm"];
                const notRedirectingToRoleUrls = ["account-confirmation", "reset-password", "reset-phone", "reset-email-continue", "reset-email-final"];
                const existingPeriod = this.$store.state.schedule.currentSchedulePeriod.date;
                if (!existingPeriod) {
                    this.$store.dispatch("schedule/updateCurrentSchedulePeriod", {date: firstDayOfCurrentMonth, verbose: this.$globalMethods.getCurrentMonthAndYear(this.$i18n.locale)});
                }

                // Public booking pages don't need auth — skip CSRF/auth checks entirely
                // Check both route name AND path (name may be undefined if router hasn't resolved yet)
                const publicPages = ["public-booking", "public-booking-cancel", "public-booking-confirm"];
                const isPublicBooking = publicPages.includes(routingName) || this.$route.path.startsWith("/booking/");
                if (isPublicBooking) {
                    this.dataIsLoaded = true;
                    this.$store.commit("authentication/setAppInitialized");
                    return;
                }

                let result = await this.$store.dispatch("authentication/getCSRFToken");
                if (!result.successfully) return;

                result = await this.$store.dispatch('authentication/checkAuthentication');
                if (!notRedirectingToRoleUrls.includes(routingName) && result.successfully) {
                    // Fast path: /role/ only needs auth check, skip heavy data preloading
                    if (routingName === "role") {
                        this.dataIsLoaded = true;
                        this.$store.commit("authentication/setAppInitialized");
                        this.hideNativeSplashScreen();
                        return;
                    }
                    result = await this.$store.dispatch("personalData/getPersonalData");
                    if (this.role === this.CONSTANTS.ROLES.FOUNDER) {
                        let result = await this.$store.dispatch("company/getCompanyData");
                        if (result.detail === "redirect_to_choose_role") this.$router.push({name: "role"});
                        if (this.companyData?.["token"]) {
                            // Categories are lightweight and must always populate, even if another
                            // promise below fails and blocks preloadSearchAndStatistics.
                            this.$store.dispatch("goodCategory/loadAllCategories");
                            const promises = [
                                this.$store.dispatch("cashRegister/getCashRegisters"),
                                this.$store.dispatch("client/getClients"),
                                this.$store.dispatch("good/getGoods"),
                                this.$store.dispatch("supplyAndWarehouseMove/getSuppliesAndWarehouseMoves"),
                                this.$store.dispatch("staff/getStaffs"),
                                this.$store.dispatch("staff/getDeletedStaffs"),
                            ];
                            if (this.companyData?.["plan"]?.["schedule_and_salary_plugin_is_active"]) {
                                const schedulePeriod = this.$store.state.schedule.currentSchedulePeriod.date || firstDayOfCurrentMonth;
                                promises.push(
                                    this.$store.dispatch("schedule/getSchedules", {dateFrom: schedulePeriod, dateTo: schedulePeriod}),
                                    this.$store.dispatch("payout/getPayouts", {targetPeriod: schedulePeriod}),
                                    this.$store.dispatch("accrual/getAccruals", {targetPeriod: schedulePeriod}),
                                    this.$store.dispatch("salaryReport/getSalaryReports", schedulePeriod),
                                );
                            }
                            const results = await Promise.all(promises);
                            const allSuccessful = results.every(r => r.successfully);
                            if (allSuccessful && this.personalData["email"] === this.companyData?.["founder"]?.["email"] && routingName === "create-company") this.$router.push({name: "dashboard"});
                            if (allSuccessful) await this.preloadSearchAndStatistics();
                            this.dataIsLoaded = true;
                        } else {
                            this.dataIsLoaded = true;
                            this.$router.push({name: "create-company"});
                        }
                    } else if (this.role === this.CONSTANTS.ROLES.STAFF) {
                        let allSuccessful = true;
                        if (this.companyData?.["token"]) {
                            let result = await this.$store.dispatch("company/getCompanyData");
                            if (result.detail === "redirect_to_choose_role" || !this.companyData) this.$router.push({name: "role"});
                            if (result.successfully) {
                                // Categories load independently of the main dispatches below.
                                this.$store.dispatch("goodCategory/loadAllCategories");
                                const promises = [
                                    this.$store.dispatch("personalData/getPersonalStaffData"),
                                    this.$store.dispatch("cashRegister/getCashRegisters"),
                                    this.$store.dispatch("client/getClients"),
                                    this.$store.dispatch("good/getGoods"),
                                    this.$store.dispatch("supplyAndWarehouseMove/getSuppliesAndWarehouseMoves"),
                                ];
                                if (this.companyData?.["plan"]?.["schedule_and_salary_plugin_is_active"]) {
                                    const schedulePeriod = this.$store.state.schedule.currentSchedulePeriod.date || firstDayOfCurrentMonth;
                                    promises.push(
                                        this.$store.dispatch("staff/getStaffs"),
                                        this.$store.dispatch("staff/getDeletedStaffs"),
                                        this.$store.dispatch("schedule/getSchedules", {dateFrom: schedulePeriod, dateTo: schedulePeriod}),
                                        this.$store.dispatch("payout/getPayouts", {targetPeriod: schedulePeriod}),
                                        this.$store.dispatch("accrual/getAccruals", {targetPeriod: schedulePeriod}),
                                        this.$store.dispatch("salaryReport/getSalaryReports", schedulePeriod),
                                    );
                                }
                                const results = await Promise.all(promises);
                                allSuccessful = results.every(r => r.successfully);
                            } else {
                                allSuccessful = false;
                            }
                        }
                        if (allSuccessful) {
                            let result = await this.$store.dispatch("company/getAvailableCompanies");
                            if (result.successfully) await this.preloadSearchAndStatistics();
                            if (result.successfully) this.dataIsLoaded = true;
                        }
                    } else if (routingName !== "role") {
                        this.dataIsLoaded = true;
                        this.$router.push({name: "role"});
                    }
                } else if (!unauthorizedAccessUrls.includes(routingName)) {
                    this.dataIsLoaded = true;
                    this.$router.push({name: 'sign-in'});
                }
                this.dataIsLoaded = true;
                this.$store.commit("authentication/setAppInitialized");
                this.hideNativeSplashScreen();
            },
            async preloadSearchAndStatistics() {
                const raw = this.$globalMethods.getDataFromLocalStorage("currentCashRegisterId");
                const parsed = raw ? Number(raw) : NaN;
                const shopId = raw ? (isNaN(parsed) ? raw : parsed) : "all";
                const currency = this.companyData?.main_currency || "USD";

                const savedPeriod = this.$globalMethods.getCookie(`savedPeriod-${shopId}`);
                let dateFrom, dateTo;
                if (savedPeriod && savedPeriod !== "today" && savedPeriod.includes("|")) {
                    [dateFrom, dateTo] = savedPeriod.split("|");
                } else {
                    const today = new Date().toISOString().split("T")[0];
                    dateFrom = today;
                    dateTo = today;
                }

                const promises = [
                    this.$store.dispatch("staff/getAllCashiers"),
                    this.$store.dispatch("good/getAllGoodsForSearch"),
                ];

                const canViewStatistics = this.role === this.CONSTANTS.ROLES.FOUNDER
                    || this.$store.state.personalData.personalStaffData?.view_statistics_permission;
                if (canViewStatistics) {
                    promises.push(this.$store.dispatch("statistics/loadAllStatistics", {dateFrom, dateTo, currency, shopId, goodFormat: "all"}));
                }

                const results = await Promise.allSettled(promises);
                results.forEach((r, i) => {
                    if (r.status === "rejected") {
                        console.error(`preloadSearchAndStatistics: promise[${i}] rejected`, r.reason);
                    }
                });
            },
            hideRecaptchaBadge() {
                const recaptchaBadge = document.querySelector(".grecaptcha-badge");
                if (recaptchaBadge) {
                    recaptchaBadge.classList.add("!hidden");
                }
            },
            hideJivoChat() {
                const jivoChatIcon = document.querySelector("jdiv");
                if (jivoChatIcon) {
                    jivoChatIcon.remove();
                }
            },
            autodetectDeviceVersion() {
                if (window.innerWidth < 1024) {
                    this.$store.dispatch("authentication/changeIsMobileVersion", true);
                } else {
                    this.$store.dispatch("authentication/changeIsMobileVersion", false);
                }
            },
            autoHideRecaptchaBadge() {
                this.$router.isReady().then(() => this.loadData());
                setTimeout(this.hideRecaptchaBadge, 100);
                setTimeout(this.hideRecaptchaBadge, 250);
                setTimeout(this.hideRecaptchaBadge, 1000);
                setTimeout(this.hideRecaptchaBadge, 1500);
                setTimeout(this.hideRecaptchaBadge, 2000);
                setTimeout(this.hideRecaptchaBadge, 2500);
                setTimeout(this.hideRecaptchaBadge, 3000);
                setTimeout(this.hideRecaptchaBadge, 3500);
                setTimeout(this.hideRecaptchaBadge, 4000);
            },
            autoHideJivoChat() {
                if (this.isMobileVersion) {
                    this._jivoChatIntervalId = setInterval(this.hideJivoChat, 500);
                }
            },
            _onResize() {
                this.autodetectDeviceVersion();
                this.autoHideRecaptchaBadge();
                this.autoHideJivoChat();
            },
        },
        async mounted() {
            this.initNativePlugins();
            this.autoDetectUserLanguage();
            this.autoHideRecaptchaBadge();
            this.autoHideJivoChat();
            this._loadDataIntervalId = setInterval(() => {
                if (!this.isAuthorized) return;
                this.loadData();
            }, UPDATE_DATA_INTERVAL);
            this.startDataPolling();

            // Подключить SSE: сразу, через 2с и 5с (на случай если данные ещё грузятся)
            this.tryConnectSSE();
            setTimeout(() => this.tryConnectSSE(), 2000);
            setTimeout(() => this.tryConnectSSE(), 5000);
        },
        created() {
            this.$store.dispatch("authentication/setIsPWA", this.$globalMethods.isPWA());
            window.addEventListener("resize", this._onResize);
            this._onSidebarToggled = (e) => { this.sidebarCollapsed = e.detail.collapsed; };
            window.addEventListener("sidebar-toggled", this._onSidebarToggled);
            this.autodetectDeviceVersion();
        },
        beforeUnmount() {
            if (this._loadDataIntervalId) clearInterval(this._loadDataIntervalId);
            if (this._dataPollingIntervalId) clearInterval(this._dataPollingIntervalId);
            if (this._jivoChatIntervalId) clearInterval(this._jivoChatIntervalId);
            window.removeEventListener("resize", this._onResize);
            window.removeEventListener("sidebar-toggled", this._onSidebarToggled);

            // Отключить SSE
            this.$store.dispatch('salesSSE/disconnect');
        }
    }
</script>
